import { createLocalTrack } from '../../features/base/lib-jitsi-meet/functions.any';
import { getPropertyValue } from '../../features/base/settings';
import { getLocalVideoTrack } from '../../features/base/tracks/functions';
// 듀얼모니터 offer 발행 디버스트: 한 페이지 최대 49개 PeerConnection 이 동시에 offer 를
// 쏘면 시그널/ICE 폭주로 일부가 연결 실패해 검은 타일이 남는다 → offer 를 최소 간격으로
// 시간 분산한다(첫 offer 는 기존과 유사한 settle 후, 이후는 STAGGER 간격).
let lastDualOfferAt = 0;
const DUAL_OFFER_BASE_MS = 800;
const DUAL_OFFER_STAGGER_MS = 120;
const waitDualOfferSlot = async () => {
    const now = Date.now();
    const earliest = Math.max(now + DUAL_OFFER_BASE_MS, lastDualOfferAt + DUAL_OFFER_STAGGER_MS);
    lastDualOfferAt = earliest;
    const wait = earliest - now;
    if (wait > 0)
        await new Promise(resolve => setTimeout(resolve, wait));
};
// 첫 연결 실패(offer 를 쐈으나 answer/ICE 미도착) 복구용 타임아웃·재시도 상한
const DUAL_CONNECT_TIMEOUT_MS = 8000;
const DUAL_MAX_OFFER_RETRIES = 2;
// 수신(원격) candidate 대기 큐 상한 — 정상 부하는 인스턴스당 ~16개(후보 4-8 × 교사측
// 이중 dispatch 2배). 상한은 relay 재전송 루프 등 비정상 폭주 방어용.
const PENDING_REMOTE_CANDIDATE_MAX = 64;
class PeerConnection {
    constructor(user_id, member_id, isAnswer, handler) {
        this.iceCandidateBuffer = [];
        // ★수신(원격) candidate 대기 큐 — remoteDescription(SRD) 설정 전에 도착한 상대 candidate 를
        // 버리지 않고 SRD 성공 직후 flush 한다(QA 실측: relay 순서 역전 → InvalidStateError 유실이
        // 듀얼 첫 연결 실패/워치독 재시도의 기여 요인). 송신용 iceCandidateBuffer 와 반드시 별도 —
        // 공유하면 수신 candidate 를 상대에게 에코 송신하는 오염이 생긴다.
        this.pendingRemoteCandidates = [];
        // 듀얼모니터 발행 시 회의(jitsi)용 로컬 트랙을 재사용한 경우 true.
        // 이 트랙은 jitsi 가 소유하므로 정리 시 stop 하면 안 된다.
        this.reusedLocalTrack = false;
        // 첫 연결 실패 복구(교사 offerer 측): 워치독 타이머 + 재시도 횟수
        this.connectWatchdog = null;
        this.offerRetries = 0;
        this.createPeerConnection = () => {
            const iceServer = getPropertyValue(APP.store.getState, 'iceServer');
            const pc = new RTCPeerConnection({
                iceServers: iceServer,
            });
            pc.onicecandidate = event => {
                if (event.candidate) {
                    // console.log(`Candidate : ${JSON.stringify(event.candidate)}`);
                    // if (event.candidate.type !== 'srflx') {
                    // 	console.log('Skip ICE candidate:', event.candidate);
                    // 	return;
                    // }
                    if (pc.remoteDescription) {
                        // console.log('New ICE candidate:', event.candidate);
                        APP.management.callCandidateMember({
                            user_uuid: this.user_id,
                            member_uuid: this.member_id,
                            ice_candidate: event.candidate,
                        });
                    }
                    else {
                        // console.log('Remote description is not set yet. Buffering ICE candidate.');
                        this.iceCandidateBuffer.push(event.candidate);
                    }
                }
                else {
                    console.log('ICE gathering complete');
                }
            };
            pc.ontrack = event => {
                // console.log(event);
                // 여기서 원격 스트림을 처리하세요
                this.track = event.streams[0];
                this.trackId = event.track.id;
                this.handler.addTrack(event.streams[0], event.track);
            };
            pc.onconnectionstatechange = ev => {
                // console.log('Connection state changed:', pc.connectionState);
                // 연결 성공 → 첫 연결 워치독 해제·재시도 카운터 리셋
                if (pc.connectionState === 'connected') {
                    clearTimeout(this.connectWatchdog);
                    this.offerRetries = 0;
                }
                if (pc.connectionState === 'disconnected') {
                    this.disconnect();
                }
                // if (pc.connectionState === 'closed' || pc.connectionState === 'failed') {
                // 	this.isClosed = true;
                // 	this.iceCandidateBuffer = [];
                // }
            };
            pc.oniceconnectionstatechange = async (ev) => {
                // console.log('ICE connection state changed:', pc.iceConnectionState);
                if (pc.iceConnectionState === 'disconnected' || pc.iceConnectionState === 'failed') {
                    if (this.isClosed || !this.peer)
                        return;
                    if (!this.isAnswer) {
                        try {
                            const peer = this.createPeerConnection();
                            this.peer = peer;
                            // ★피어 세대 교체 — 구세대 candidate 잔존 주입/송신 방지(recreateOfferPeer 와 동일)
                            this.pendingRemoteCandidates = [];
                            this.iceCandidateBuffer = [];
                            await new Promise(resolve => {
                                let timer;
                                const onTime = () => {
                                    if (pc.signalingState === 'stable') {
                                        clearTimeout(timer);
                                        return resolve();
                                    }
                                    timer = setTimeout(onTime, 1000);
                                };
                                timer = setTimeout(onTime, 1000);
                            });
                            const offer = await peer.createOffer({ iceRestart: true });
                            await peer.setLocalDescription(offer);
                            APP.management.callOfferMember({
                                user_uuid: this.user_id,
                                member_uuid: this.member_id,
                                offer: peer.localDescription,
                            });
                        }
                        catch (err) {
                            console.error('OfferRetry:', err);
                        }
                    }
                }
            };
            pc.onsignalingstatechange = ev => {
                // console.log('Signaling state changed:', pc.signalingState);
                if (pc.signalingState === 'closed') {
                    this.iceCandidateBuffer = [];
                }
            };
            pc.onicegatheringstatechange = event => {
                if (pc.iceGatheringState === 'complete') {
                    // 여기서 연결 시도
                }
            };
            return pc;
        };
        this.user_id = user_id;
        this.member_id = member_id;
        this.isAnswer = isAnswer;
        this.iceCandidateBuffer = [];
        this.handler = handler;
        this.isClosed = false;
        this.createPeerConnection = this.createPeerConnection.bind(this);
        this.muteVideo = this.muteVideo.bind(this);
    }
    checkMember(memberId) {
        return this.member_id === memberId;
    }
    muteVideo(isMuted) {
        const sender = this.peer.getSenders().find(s => s.track && s.track.kind === 'video');
        if (sender && sender.track) {
            sender.track.enabled = !isMuted;
        }
        else if (this.track && this.track.track) {
            this.track.track.enabled = !isMuted;
        }
        else {
            console.log('No video track found to mute/unmute');
        }
        this.handler && this.handler?.isMuted(isMuted);
    }
    async replaceTrack(newTrackId) {
        if (!newTrackId || newTrackId === this.trackId) {
            // console.log('New track ID is the same or not provided. No replacement needed.');
            return;
        }
        try {
            // Create a new track with the specified resolution
            const newTrack = await createLocalTrack('video', newTrackId, 5000, {
                resolution: 180,
            });
            if (!newTrack || !newTrack.track) {
                throw new Error('Failed to create new track');
            }
            const sender = this.peer.getSenders().find(s => s.track && s.track.kind === 'video');
            if (sender) {
                try {
                    await sender.replaceTrack(newTrack.track);
                    console.log('Track replaced successfully');
                }
                catch (error) {
                    console.error('Error replacing track:', error);
                }
            }
            else {
                // console.log('No video sender found. Adding new track.');
                this.peer.addTrack(newTrack.track, newTrack.stream);
            }
            // 트랙 교체 후 인코딩 제약 재적용 — replaceTrack 은 기존 SendEncodings 를 유지하므로
            // (이전에 720 기준으로 잡힌 scaleResolutionDownBy 가 이미 180p 인 새 트랙에 겹쳐
            // 과다 축소되던 것을 새 트랙 높이 기준으로 재계산).
            await this.applyDualSenderEncoding(180);
            // Update the current track reference
            // 재사용한 jitsi 트랙은 jitsi 가 소유하므로 stop 하지 않는다.
            if (this.track && this.track.track && !this.reusedLocalTrack) {
                this.track.track.stop(); // Stop the old track
            }
            this.reusedLocalTrack = false;
            this.track = newTrack;
            this.trackId = newTrackId;
        }
        catch (error) {
            // console.error('Error replacing track:', error);
        }
    }
    // 듀얼모니터 송신 화질 제약. 트랙(capture)은 jitsi 가 소유해 바꿀 수 없으므로,
    // 이 듀얼 PeerConnection 의 video sender 인코딩만 저해상도로 다운스케일한다.
    // (WebRTC 는 sender 별 독립 인코딩 → jitsi 원본 화질은 그대로, 듀얼 송신만 180p)
    // 교사가 수십 명을 동시에 받는데 각자 720p 로 보내면 디코드/대역폭 과부하로 타일이
    // 검게 나온다 — 작게 보내 다인원에서도 뜨게 한다.
    async applyDualSenderEncoding(targetHeight = 180) {
        const sender = this.peer?.getSenders().find(s => s.track && s.track.kind === 'video');
        if (!sender || !sender.track)
            return;
        try {
            const settings = sender.track.getSettings?.() || {};
            // 소스 높이를 모르면 학생 캡처 기본값(720)으로 가정
            const sourceHeight = settings.height || 720;
            const scale = sourceHeight > targetHeight ? sourceHeight / targetHeight : 1;
            const params = sender.getParameters();
            // 인코딩 구조 변경(추가/삭제)은 금지 — 기존 인코딩 필드만 수정한다
            if (!params.encodings || params.encodings.length === 0)
                return;
            params.encodings.forEach((enc) => {
                enc.scaleResolutionDownBy = scale;
                enc.maxBitrate = 200000; // 200kbps
                enc.maxFramerate = 15;
            });
            await sender.setParameters(params);
        }
        catch (e) {
            console.error('듀얼 송신 180p 제약 적용 실패:', e);
        }
    }
    // 교사(recvonly) offer 발행. iceRestart 여부 선택.
    async emitOffer(iceRestart = false) {
        const peer = this.peer;
        if (!peer || this.isClosed)
            return;
        const offer = await peer.createOffer(iceRestart ? { iceRestart: true } : undefined);
        await peer.setLocalDescription(offer);
        APP.management.callOfferMember({
            user_uuid: this.user_id,
            member_uuid: this.member_id,
            offer: peer.localDescription,
        });
    }
    // 첫 연결 실패 타일 복구: 피어를 새로 만들고(recvonly) offer 를 재발행한다.
    // (never-answered 피어는 have-local-offer 에 갇혀 재offer 가 불가하므로 재생성이 정석)
    async recreateOfferPeer() {
        const oldPeer = this.peer;
        const peer = this.createPeerConnection();
        this.peer = peer;
        // ★피어 세대 교체 — 구세대 candidate(수신 큐·송신 버퍼)가 신 세션에 주입/송신되지
        // 않도록 양방향 모두 클리어(ufrag 불일치 주입은 checking 을 연장시켜 워치독 재시도를
        // 오히려 늘린다). 큐 내용물은 정의상 전부 구세대라 버려도 잃는 것이 없다.
        this.pendingRemoteCandidates = [];
        this.iceCandidateBuffer = [];
        peer.addTransceiver('video', { direction: 'recvonly' });
        try {
            oldPeer && oldPeer.close();
        }
        catch (e) {
            // noop
        }
        await this.emitOffer(false);
    }
    // offer 를 쏜 뒤 일정 시간 내 connected 안 되면 재시도(상한까지). connected 시 해제.
    startConnectWatchdog() {
        if (this.isAnswer)
            return; // 교사(offerer) 측만
        clearTimeout(this.connectWatchdog);
        this.connectWatchdog = setTimeout(async () => {
            if (this.isClosed || !this.peer)
                return;
            if (this.peer.connectionState === 'connected')
                return;
            if (this.offerRetries >= DUAL_MAX_OFFER_RETRIES) {
                console.warn('[dual] 연결 재시도 소진 — 타일 검정 잔존:', this.member_id);
                return;
            }
            this.offerRetries += 1;
            console.warn('[dual] 첫 연결 실패 재시도', this.offerRetries, this.member_id);
            try {
                await this.recreateOfferPeer();
            }
            catch (e) {
                console.error('[dual] 재offer 실패:', e);
            }
            this.startConnectWatchdog(); // 다음 재시도 예약
        }, DUAL_CONNECT_TIMEOUT_MS);
    }
    async initLocalStream(userSelectedCameraDeviceId, offer) {
        const peer = this.createPeerConnection();
        this.peer = peer;
        // 신 피어 시작 — 이전 잔존 수신 candidate 방어적 클리어(정상 흐름에선 비어 있음)
        this.pendingRemoteCandidates = [];
        let localTrack = null;
        if (userSelectedCameraDeviceId) {
            // iPad/iOS 등은 동일 카메라를 동시에 두 번 getUserMedia 할 수 없다.
            // 듀얼모니터 발행용으로 카메라를 다시 열면(=두 번째 캡쳐) iPad 에선 검정 화면이
            // 되므로, 회의(jitsi)용으로 이미 캡쳐된 로컬 비디오 트랙을 재사용한다.
            let reused = false;
            try {
                const localVideo = getLocalVideoTrack(APP.store.getState()['features/base/tracks'])?.track;
                const mediaTrack = localVideo?.track; // MediaStreamTrack
                const mediaStream = localVideo?.stream; // MediaStream
                if (mediaTrack && mediaTrack.readyState === 'live') {
                    const stream = mediaStream || new MediaStream([mediaTrack]);
                    peer.addTrack(mediaTrack, stream);
                    this.trackId = userSelectedCameraDeviceId;
                    this.track = stream;
                    this.reusedLocalTrack = true;
                    reused = true;
                }
            }
            catch (err) {
                console.error('Reuse jitsi local video for dual failed:', err);
            }
            // 예외적으로 jitsi 로컬 트랙이 아직 없을 때만 별도 캡쳐로 폴백
            if (!reused) {
                const tracks = await createLocalTrack('video', userSelectedCameraDeviceId, 5000, {
                    resolution: 360,
                });
                await peer.addTrack(tracks.track, tracks.stream);
                this.trackId = tracks.deviceId;
                this.track = tracks; // Update the current track reference
                localTrack = tracks; // Assuming createLocalTrack returns an array of tracks
            }
        }
        if (localTrack) {
            this.track = localTrack.stream;
        }
        if (this.isAnswer) {
            try {
                await peer.setRemoteDescription(new RTCSessionDescription(offer));
                // ★학생(answerer) 측 SRD 는 이 인라인 경로뿐(setRemoteDescription 메서드는 교사
                // answer 수신 전용) — SRD 전에 큐잉된 교사 candidate 를 여기서 주입한다.
                // 주의: 이 SRD 를 아래 setRemoteDescription 메서드 재사용으로 리팩터하면
                // 수신 flush 순서가 answer 송신보다 앞서게 되는 순서역전이 생기므로 금지.
                await this.flushPendingRemoteCandidates(peer);
                const answer = await peer.createAnswer();
                await peer.setLocalDescription(answer);
                // 듀얼모니터 송신은 180p 로 제약(다인원 검은화면 방지) — sender 협상 완료 후 적용.
                await this.applyDualSenderEncoding(180);
                APP.management.callAnswerMember({
                    user_uuid: this.user_id,
                    member_uuid: this.member_id,
                    answer: peer.localDescription,
                });
                const iceCandidates = [...this.iceCandidateBuffer];
                this.iceCandidateBuffer = [];
                for (const iceCandidate of iceCandidates) {
                    // console.log('New ICE candidate:', iceCandidate);
                    APP.management.callCandidateMember({
                        user_uuid: this.user_id,
                        member_uuid: this.member_id,
                        ice_candidate: iceCandidate,
                    });
                }
            }
            catch (err) {
                console.error('Answer:', err);
            }
        }
        else {
            peer.addTransceiver('video', { direction: 'recvonly' });
            try {
                // 동시 49개 offer 폭주를 시간 분산(디버스트) — 셋업 경합으로 인한 검은 타일 감소
                await waitDualOfferSlot();
                await this.emitOffer(false);
                // 첫 연결 실패(answer/ICE 미도착) 타일 자동 복구 워치독 시작
                this.startConnectWatchdog();
            }
            catch (err) {
                console.error('Offer:', err);
            }
        }
    }
    // SRD 성공 직후에만 호출. SRD 시점의 peer 스냅샷 기준으로만 주입하고, 도중 피어
    // 교체/종료가 감지되면 잔여를 드랍한다(구세대 candidate 주입 방지). 개별 실패는
    // 나머지 flush 를 막지 않는다.
    async flushPendingRemoteCandidates(peer) {
        const queued = this.pendingRemoteCandidates;
        if (queued.length === 0)
            return;
        this.pendingRemoteCandidates = [];
        for (const candidate of queued) {
            if (this.isClosed || this.peer !== peer)
                return;
            try {
                await peer.addIceCandidate(candidate);
            }
            catch (err) {
                console.error('IceCandidate(flush):', err);
            }
        }
    }
    async addIceCandidate(iceCandidate) {
        if (this.isClosed || !this.peer || !iceCandidate) {
            return;
        }
        const peer = this.peer;
        // ★SRD(answer/offer 적용) 전에 도착한 상대 candidate 는 큐잉 — 종전엔 즉시
        // addIceCandidate 를 호출해 InvalidStateError(remote description null)로 유실됐고
        // (QA 로그 실측), 인원이 많아 answer 가 늦어질수록 유실이 커져 듀얼 첫 연결 실패의
        // 기여 요인이었다. SRD 성공 직후 flushPendingRemoteCandidates 가 주입한다.
        if (!peer.remoteDescription) {
            if (this.pendingRemoteCandidates.length >= PENDING_REMOTE_CANDIDATE_MAX) {
                console.warn('[dual] 수신 candidate 큐 상한 초과 — 드랍:', this.member_id);
                return;
            }
            // 교사 측은 같은 candidate 가 이중 dispatch(publish + 직접 루프)로 2회 들어온다 — dedup.
            // (이중 dispatch 자체는 DualMemeber 의 peer state 커밋 전 공백을 커버하는 의도적 중복이라 유지)
            const key = `${iceCandidate.candidate ?? ''}|${iceCandidate.sdpMid ?? ''}|${iceCandidate.sdpMLineIndex ?? ''}`;
            const exists = this.pendingRemoteCandidates.some(c => `${c.candidate ?? ''}|${c.sdpMid ?? ''}|${c.sdpMLineIndex ?? ''}` === key);
            if (!exists)
                this.pendingRemoteCandidates.push(iceCandidate);
            return;
        }
        try {
            // console.log('Adding ice candidate:', iceCandidate);
            // let ipv6 = false;
            // split(iceCandidate.candidate, ' ').forEach((item, index) => {
            // 	if (index === 4) {
            // 		if (split(item, ':').length  > 1) {
            // 			ipv6 = true;
            // 		}
            // 	}
            // });
            // if (ipv6) {
            // 	console.log('Skip IPv6:', iceCandidate);
            // 	return;
            // }
            await peer.addIceCandidate(iceCandidate);
        }
        catch (err) {
            console.error('IceCandidate:', err);
        }
    }
    async setRemoteDescription(description) {
        if (this.isClosed || !this.peer) {
            return;
        }
        const peer = this.peer;
        try {
            // console.log('Setting remote description:', description);
            await peer.setRemoteDescription(new RTCSessionDescription(description));
            // ★SRD 전에 도착해 큐잉된 수신 candidate 를 먼저 주입(유실 방지) — 교사(answer 수신) 경로
            await this.flushPendingRemoteCandidates(peer);
            const iceCandidates = [...this.iceCandidateBuffer];
            this.iceCandidateBuffer = [];
            for (const iceCandidate of iceCandidates) {
                // console.log('New ICE candidate:', iceCandidate);
                APP.management.callCandidateMember({
                    user_uuid: this.user_id,
                    member_uuid: this.member_id,
                    ice_candidate: iceCandidate,
                });
            }
        }
        catch (err) {
            console.error(':', err);
        }
    }
    disconnect() {
        // 첫 연결 워치독 정리(퇴장/페이지 이동 후 늦은 재offer 방지)
        clearTimeout(this.connectWatchdog);
        this.connectWatchdog = null;
        if (this.peer) {
            this.peer.close();
            this.peer = null;
            this.isClosed = true;
            this.iceCandidateBuffer = [];
            this.pendingRemoteCandidates = [];
            if (!Array.isArray(APP.peerconnections)) {
                APP.peerconnections = [];
            }
            const currentUserId = this.user_id;
            APP.peerconnections = APP.peerconnections.filter(p => {
                if (p && typeof p === 'object' && 'user_id' in p) {
                    return p.user_id !== currentUserId;
                }
                return false; // 유효하지 않은 peer 객체는 제외
            });
        }
    }
}
export default PeerConnection;
